import { isAxiosError } from 'axios'

/**
 * Read a Blob as text across environments. Prefers `Blob.text()` (modern
 * browsers) and falls back to `FileReader` for webviews / test envs (jsdom)
 * where `.text()` is missing or returns nothing.
 */
export const readBlobText = async (blob: Blob): Promise<string> => {
  if (typeof blob.text === 'function') {
    try {
      const text = await blob.text()
      if (text) return text
    } catch {
      // fall through to FileReader
    }
  }
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result ?? ''))
    reader.onerror = () => reject(reader.error)
    reader.readAsText(blob)
  })
}

/**
 * Recover the real error envelope from a `responseType: 'blob'` request.
 *
 * When a request asks axios for a Blob (file/report downloads), axios wraps the
 * ERROR response body in a Blob too — so `error.response.data` is a Blob whose
 * `.type` is `application/json`, and the real backend envelope
 * (`{ error_code, message, details, sentry_id, ... }`) is trapped inside it.
 * `RequestErrorHandler` runs synchronously and can't read a Blob, so it
 * enumerates the Blob's own properties and degrades the toast to the useless
 * line `type: application/json` (the real message — e.g. the export-already-
 * running 429 — is lost). See AABT-37.
 *
 * Call this in the `catch` of a blob request BEFORE re-throwing: it reads the
 * Blob, parses the JSON, and swaps `error.response.data` back to the plain
 * envelope so the normal formatter surfaces the real message. It is a no-op for
 * non-axios errors, non-blob bodies, and non-JSON blobs (a genuine file blob is
 * left untouched), and it never throws — on any failure the original error is
 * left as-is for the handler's generic fallback.
 */
export const parseBlobErrorResponse = async (error: unknown): Promise<void> => {
  if (!isAxiosError(error)) return

  const response = error.response
  const data = response?.data
  if (!(data instanceof Blob)) return

  // Only JSON blobs carry an error envelope. `type` can be empty for a
  // constructed Blob, so treat "unknown type" as "worth trying" and bail only
  // when it is explicitly a non-JSON content type.
  if (data.type && !data.type.includes('json')) return

  try {
    const text = await readBlobText(data)
    if (!text) return
    // Reassign in place so the existing RequestErrorHandler sees the normal
    // `{ error_code, message, ... }` shape instead of a Blob.
    response!.data = JSON.parse(text)
  } catch {
    // Not actually JSON — leave the Blob in place; the handler falls back to
    // its generic message rather than crashing.
  }
}
